import PropTypes from "prop-types";
import { useState, useCallback, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { DatePicker, Popover, TextField, Icon } from "@shopify/polaris";
import { CalendarIcon } from "@shopify/polaris-icons";
import { toDateString, fromDateString } from "../../../utils/dateString";

const formatForDisplay = (date) => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

RHFDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  minDate: PropTypes.instanceOf(Date),
  shouldDisableDate: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disabled: PropTypes.bool,
  // TODO: remove outputFormat prop after V1 deprecation — flip default to "string"
  outputFormat: PropTypes.oneOf(["string", "date"]),
};

export default function RHFDatePicker({ name, minDate, shouldDisableDate, label, helpText, disabled, outputFormat = "date" }) {
  const { control, watch } = useFormContext();
  const [popoverActive, setPopoverActive] = useState(false);
  const today = new Date();

  const selectedDateValue = watch(name);

  const toDisplayDate = useCallback((val) => {
    if (!val) return null;
    if (outputFormat === "string" && typeof val === "string") return fromDateString(val);
    return new Date(val);
  }, [outputFormat]);

  const initialDate = toDisplayDate(selectedDateValue) || today;
  const [calendarMonth, setCalendarMonth] = useState(initialDate.getMonth());
  const [calendarYear, setCalendarYear] = useState(initialDate.getFullYear());

  useEffect(() => {
    const d = toDisplayDate(selectedDateValue);
    if (d) {
      setCalendarMonth(d.getMonth());
      setCalendarYear(d.getFullYear());
    }
  }, [selectedDateValue, toDisplayDate]);

  const togglePopover = useCallback(() => setPopoverActive((active) => !active), []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const selectedDate = toDisplayDate(value);

        return (
          <Popover
            active={popoverActive}
            activator={
              <TextField
                label={label}
                value={selectedDate ? formatForDisplay(selectedDate) : ""}
                onFocus={togglePopover}
                onChange={() => {}}
                error={error?.message}
                helpText={helpText}
                disabled={disabled}
                autoComplete="off"
                suffix={<Icon source={CalendarIcon} color="base" />}
              />
            }
            onClose={togglePopover}
          >
            <Popover.Section>
              <DatePicker
                month={calendarMonth}
                year={calendarYear}
                selected={selectedDate ?? today}
                onChange={(date) => {
                  onChange(outputFormat === "string" ? toDateString(date.start) : date.start);
                  setPopoverActive(false);
                }}
                onMonthChange={(month, year) => {
                  setCalendarMonth(month);
                  setCalendarYear(year);
                }}
                disableDatesBefore={minDate}
                disableDates={shouldDisableDate}
              />
            </Popover.Section>
          </Popover>
        );
      }}
    />
  );
}